import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle, XCircle } from "lucide-react";
import { api } from "../lib/api";

/**
 * Lands here from the verification email link. Pulls the token from the
 * query string, hits /api/auth/verify, then shows a friendly result.
 */
export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [state, setState] = useState({ loading: true, ok: false, error: "", email: "" });

  useEffect(() => {
    if (!token) {
      setState({ loading: false, ok: false, error: "Missing verification token in URL.", email: "" });
      return;
    }
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/auth/verify?token=${encodeURIComponent(token)}`);
        if (!alive) return;
        setState({ loading: false, ok: true, error: "", email: data.email || "" });
      } catch (e) {
        if (!alive) return;
        const detail = e?.response?.data?.detail || "Could not verify this link.";
        setState({ loading: false, ok: false, error: detail, email: "" });
      }
    })();
    return () => { alive = false; };
  }, [token]);

  return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <div className="max-w-md w-full border-2 border-kult-border p-8 bg-kult-black2" data-testid="verify-email-page">
        <p className="font-mono text-xs tracking-[0.3em] text-kult-yellow mb-4">/ ACCOUNT VERIFICATION</p>
        {state.loading ? (
          <p className="font-mono text-sm text-kult-muted" data-testid="verify-loading">Confirming your email...</p>
        ) : state.ok ? (
          <>
            <div className="flex items-center gap-3 mb-3">
              <CheckCircle size={28} className="text-kult-yellow" />
              <h1 className="font-display text-3xl">EMAIL CONFIRMED</h1>
            </div>
            <p className="text-kult-muted text-sm">
              {state.email && <><strong className="text-white">{state.email}</strong> is verified. </>}
              You're all set. Log in to your portal whenever you're ready.
            </p>
            <Link
              to="/portal/login"
              className="inline-block mt-6 px-5 py-3 bg-kult-pink text-black font-mono text-xs tracking-[0.25em] hover:bg-white"
              data-testid="verify-go-to-portal"
            >
              GO TO PORTAL →
            </Link>
          </>
        ) : (
          <>
            <div className="flex items-center gap-3 mb-3">
              <XCircle size={28} className="text-kult-pink" />
              <h1 className="font-display text-3xl">LINK INVALID</h1>
            </div>
            <p className="text-kult-muted text-sm" data-testid="verify-error">{state.error}</p>
            <Link
              to="/portal/login"
              className="inline-block mt-6 px-5 py-3 border border-kult-border text-white font-mono text-xs tracking-[0.25em] hover:border-kult-pink"
            >
              GO TO PORTAL
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
